import colors from "../base/colors";
import typography from "../base/typography";
import borders from "../base/borders";

import pxToRem from "../functions/pxToRem";

const { white, text, grey, transparent, primary, secondary, info, success, warning, error } =
  colors;
const { size } = typography;
const { borderRadius } = borders;

type SemanticColor = {
  main: string;
  dark?: string;
  focus?: string;
  light?: string;
  contrastText: string;
};

function buildSemanticChipVariants(palette: Record<string, SemanticColor>) {
  const variants: Record<string, object> = {};

  Object.entries(palette).forEach(([name, tone]) => {
    const key = name.charAt(0).toUpperCase() + name.slice(1);
    const hoverBg = tone.dark ?? tone.focus ?? tone.main;
    const outlinedText = tone.light ?? tone.main;

    variants[`filled${key}`] = {
      backgroundColor: tone.main,
      color: tone.contrastText,
      "&:hover": {
        backgroundColor: hoverBg,
      },
      "& .MuiChip-icon, & .MuiChip-deleteIcon": {
        color: tone.contrastText,
      },
      "& .MuiChip-deleteIcon:hover": {
        color: tone.contrastText,
      },
    };

    variants[`outlined${key}`] = {
      backgroundColor: transparent.main,
      borderColor: tone.main,
      color: outlinedText,
      "&:hover": {
        backgroundColor: `${tone.main}24`,
        borderColor: outlinedText,
      },
      "& .MuiChip-icon, & .MuiChip-deleteIcon": {
        color: outlinedText,
      },
      "& .MuiChip-deleteIcon:hover": {
        color: tone.main,
      },
    };
  });

  return variants;
}

const semanticVariants = buildSemanticChipVariants({
  primary,
  secondary,
  info,
  success,
  warning,
  error,
});

const chip = {
  styleOverrides: {
    root: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      height: pxToRem(32),
      color: text.primary,
      backgroundColor: grey[200],
      borderRadius: borderRadius.lg,
      whiteSpace: "nowrap",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",

      "&:hover": {
        backgroundColor: grey[300],
      },

      "&:focus": {
        backgroundColor: grey[300],
      },

      "& .MuiChip-icon": {
        color: "inherit",
        fontSize: pxToRem(16),
      },

      "& .MuiChip-deleteIcon": {
        color: "inherit",
        fontSize: pxToRem(16),
        opacity: 0.72,
        "&:hover": {
          color: text.primary,
          opacity: 1,
        },
      },
    },

    label: {
      color: "inherit",
    },

    sizeSmall: {
      height: pxToRem(24),
      fontSize: size.xs,

      "& .MuiChip-icon": {
        fontSize: pxToRem(14),
      },

      "& .MuiChip-deleteIcon": {
        fontSize: pxToRem(14),
      },
    },

    sizeMedium: {
      height: pxToRem(32),
      fontSize: size.sm,

      "& .MuiChip-icon": {
        fontSize: pxToRem(16),
      },

      "& .MuiChip-deleteIcon": {
        fontSize: pxToRem(16),
      },
    },

    ...semanticVariants,

    // Priority colors (dark theme)
    priorityCritical: {
      backgroundColor: colors.priority.critical.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.priority.critical.focus,
      },
    },

    priorityHigh: {
      backgroundColor: colors.priority.high.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.priority.high.focus,
      },
    },

    priorityMedium: {
      backgroundColor: colors.priority.medium.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.priority.medium.focus,
      },
    },

    priorityLow: {
      backgroundColor: colors.priority.low.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.priority.low.focus,
      },
    },

    // Status colors (dark theme)
    statusNew: {
      backgroundColor: colors.status.new.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.status.new.focus,
      },
    },

    statusAssigned: {
      backgroundColor: colors.status.assigned.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.status.assigned.focus,
      },
    },

    statusInProgress: {
      backgroundColor: colors.status.inProgress.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.status.inProgress.focus,
      },
    },

    statusOnHold: {
      backgroundColor: colors.status.onHold.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.status.onHold.focus,
      },
    },

    statusResolved: {
      backgroundColor: colors.status.resolved.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.status.resolved.focus,
      },
    },

    statusClosed: {
      backgroundColor: colors.status.closed.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.status.closed.focus,
      },
    },

    statusCancelled: {
      backgroundColor: colors.status.cancelled.main,
      color: white.main,
      "&:hover": {
        backgroundColor: colors.status.cancelled.focus,
      },
    },

    // Outlined variants (dark theme)
    outlined: {
      backgroundColor: transparent.main,
      border: `1px solid ${grey[500]}`,
      color: text.primary,

      "&:hover": {
        backgroundColor: grey[200],
        borderColor: grey[600],
      },
    },

    outlinedPriorityCritical: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.priority.critical.main}`,
      color: colors.priority.critical.main,
      "&:hover": {
        backgroundColor: colors.priority.critical.main,
        color: white.main,
      },
    },

    outlinedPriorityHigh: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.priority.high.main}`,
      color: colors.priority.high.main,
      "&:hover": {
        backgroundColor: colors.priority.high.main,
        color: white.main,
      },
    },

    outlinedPriorityMedium: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.priority.medium.main}`,
      color: colors.priority.medium.main,
      "&:hover": {
        backgroundColor: colors.priority.medium.main,
        color: white.main,
      },
    },

    outlinedPriorityLow: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.priority.low.main}`,
      color: colors.priority.low.main,
      "&:hover": {
        backgroundColor: colors.priority.low.main,
        color: white.main,
      },
    },

    outlinedStatusNew: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.status.new.main}`,
      color: colors.status.new.main,
      "&:hover": {
        backgroundColor: colors.status.new.main,
        color: white.main,
      },
    },

    outlinedStatusAssigned: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.status.assigned.main}`,
      color: colors.status.assigned.main,
      "&:hover": {
        backgroundColor: colors.status.assigned.main,
        color: white.main,
      },
    },

    outlinedStatusInProgress: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.status.inProgress.main}`,
      color: colors.status.inProgress.main,
      "&:hover": {
        backgroundColor: colors.status.inProgress.main,
        color: white.main,
      },
    },

    outlinedStatusOnHold: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.status.onHold.main}`,
      color: colors.status.onHold.main,
      "&:hover": {
        backgroundColor: colors.status.onHold.main,
        color: white.main,
      },
    },

    outlinedStatusResolved: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.status.resolved.main}`,
      color: colors.status.resolved.main,
      "&:hover": {
        backgroundColor: colors.status.resolved.main,
        color: white.main,
      },
    },

    outlinedStatusClosed: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.status.closed.main}`,
      color: colors.status.closed.main,
      "&:hover": {
        backgroundColor: colors.status.closed.main,
        color: white.main,
      },
    },

    outlinedStatusCancelled: {
      backgroundColor: transparent.main,
      border: `1px solid ${colors.status.cancelled.main}`,
      color: colors.status.cancelled.main,
      "&:hover": {
        backgroundColor: colors.status.cancelled.main,
        color: white.main,
      },
    },
  },
};

export default chip;
